import { parsePhoneNumber } from 'libphonenumber-js';
import type { FieldValues } from 'react-hook-form';
import validator from 'validator';
import { defaultErrorMessages } from '../config/defaultContent';
import type { ErrorMessages, Question, Rules, RulesInContent } from '../config/types';
import { defaultReservedCharacters } from '../constants';

export const getErrorMessage = (
  name: RulesInContent,
  label: string,
  value: string,
  errorMessage?: string,
) =>
  String(errorMessage || defaultErrorMessages[name] || '')
    .replace('$LABEL', label)
    .replace('$CONDITION', value);

export const isPostcode = (value: string) => {
  const re = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i;
  return re.test(value);
};

export const isValidPassword = (value: string) => {
  const re =
    /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&+=)(\-_*])([a-zA-Z0-9!@#$%^&+=*)(\-_]){8,}$/;
  return re.test(value);
};

export const validateMobile = (value: string) => {
  const defaultCountryCode = 'GB';

  if (!value.startsWith('+') && !value.startsWith('0')) {
    return false;
  }

  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountryCode);
    return phoneNumber?.isValid();
  } catch (_) {
    return false;
  }
};

export const isMinLength = (value: string | number, minLength: number) => String(value).length >= minLength;

export const isMaxLength = (value: string | number, maxLength: number) => String(value).length <= maxLength;

export const isMinNumber = (value: string, minNumber: number) =>
  validator.isNumeric(value) && Number(value) >= minNumber;

export const isMaxNumber = (value: string, maxNumber: number) =>
  validator.isNumeric(value) && Number(value) <= maxNumber;

export const matchesPattern = (value: string, pattern: any) => {
  const re = new RegExp(pattern);
  return re.test(value);
};

export const containsReservedCharacters = (
  value: string,
  reservedCharacters: string = defaultReservedCharacters,
) => {
  const escapedReservedCharacters = reservedCharacters.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
  return !new RegExp(`^[^${escapedReservedCharacters}]*$`).test(value);
};

export const hasValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length > 0;
  }
  return false;
};

export const validateField = (
  title: string,
  rules: Rules,
  value: string,
  errorMessages?: ErrorMessages,
) => {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;

  if (rules.required && !hasValue(trimmedValue)) {
    return getErrorMessage('required', title, '', errorMessages?.required || '');
  }

  if (!rules.required && !hasValue(trimmedValue)) {
    // allow empty optional values
    return undefined;
  }

  if (
    rules.reservedCharacters &&
    containsReservedCharacters(trimmedValue, rules.reservedCharacters)
  ) {
    return getErrorMessage(
      'reservedCharacters',
      title,
      '',
      errorMessages?.reservedCharacters || '',
    );
  }

  if (rules.email && !validator.isEmail(trimmedValue)) {
    return getErrorMessage('email', title, '', errorMessages?.email || '');
  }

  if (rules.number && !validator.isNumeric(trimmedValue)) {
    return getErrorMessage('number', title, '', errorMessages?.number || '');
  }

  if (rules.telephone && !validateMobile(trimmedValue)) {
    return getErrorMessage('telephone', title, '', errorMessages?.telephone || '');
  }

  if (rules.minLength && !isMinLength(trimmedValue, Number(rules.minLength))) {
    return getErrorMessage(
      'minLength',
      title,
      String(rules.minLength),
      errorMessages?.minLength || '',
    );
  }

  if (rules.maxLength && !isMaxLength(trimmedValue, Number(rules.maxLength))) {
    return getErrorMessage(
      'maxLength',
      title,
      String(rules.maxLength),
      errorMessages?.maxLength || '',
    );
  }

  if (rules.min && !isMinNumber(trimmedValue, Number(rules.min))) {
    return getErrorMessage('min', title, String(rules.min), errorMessages?.min || '');
  }

  if (rules.max && !isMaxNumber(trimmedValue, Number(rules.max))) {
    return getErrorMessage('max', title, String(rules.max), errorMessages?.max || '');
  }

  if (rules.pattern && !matchesPattern(trimmedValue, rules.pattern)) {
    return getErrorMessage('pattern', title, '', errorMessages?.pattern || '');
  }

  return undefined;
};

export const getInputRules = (rules: Rules, title: string, defaultMaxLength: number) => {
  const specialCharacterValidation = (value: string) =>
    !containsReservedCharacters(value, rules.reservedCharacters) ||
    getErrorMessage('reservedCharacters', title, '');

  return {
    ...rules,
    validate: { specialCharacterValidation },
    maxLength: rules.maxLength || defaultMaxLength,
  };
};

export const formDataToObject = (formData: [string, FormDataEntryValue][]) =>
  formData.reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, FormDataEntryValue>,
  );

export const getListAnswers = (formValues: FieldValues, questions: Question[]) => {
  const groupedAnswers: {
    [key: string]: { question: Question; questionName: string; answer: string; id: string }[];
  } = {};

  for (const [questionName, answer] of Object.entries(formValues)) {
    const question = questions.find((q) => questionName.endsWith(q.name));
    const id = questionName.split('-')[0];

    if (question) {
      if (!groupedAnswers[id]) groupedAnswers[id] = [];
      groupedAnswers[id].push({ question, questionName, answer, id });
    }
  }

  return groupedAnswers;
};

export const filterObjectByKeys = (object: Record<string, string>, keys: string[]) => {
  return Object.keys(object)
    .filter((key) => keys.includes(key))
    .reduce(
      (acc, key) => {
        acc[key] = object[key];
        return acc;
      },
      {} as Record<string, string>,
    );
};

export const groupValidation = ({
  value,
  answers,
  question,
  sumColumns,
}: {
  value: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  sumColumns?: string[];
}): string | boolean => {
  const customValidation = question?.customValidation;
  // If sumColumns is provided, filter the value object to only include the sumColumns
  // Otherwise, use the entire value object
  const values = sumColumns ? filterObjectByKeys(value, sumColumns) : value;

  if (customValidation?.fieldsEqual) {
    for (const { fields, answer, answerValue, errorPrefix } of customValidation.fieldsEqual) {
      const { success, errorMessage } = fieldCompare({
        values,
        answers,
        question,
        fields,
        answer,
        answerValue,
        errorPrefix,
      });
      if (!success) return errorMessage || '';
    }
  }

  if (customValidation?.fieldsLessThanOrEqual) {
    for (const {
      fields,
      answer,
      answerValue,
      errorPrefix,
    } of customValidation.fieldsLessThanOrEqual) {
      const { success, errorMessage } = fieldCompare({
        values,
        answers,
        question,
        fields,
        answer,
        answerValue,
        errorPrefix,
        operator: 'lessThanOrEqual',
      });
      if (!success) return errorMessage || '';
    }
  }

  if (customValidation?.sumEqualToPreviousAnswer) {
    const { success, errorMessage } = sumEqualToPreviousAnswer({
      values,
      answers,
      question,
      previousAnswer: customValidation.sumEqualToPreviousAnswer,
    });
    if (!success) return errorMessage || '';
  }

  if (customValidation?.sumLessThanOrEqualToPreviousAnswer) {
    const { success, errorMessage } = sumLessThanOrEqualToPreviousAnswer({
      values,
      answers,
      question,
      previousAnswer: customValidation.sumLessThanOrEqualToPreviousAnswer,
    });
    if (!success) return errorMessage || '';
  }

  return true;
};

export const checkAllFieldsHaveValues = ({
  value,
  keys,
}: {
  value: Record<string, string>;
  keys: string[];
}) => {
  const allFieldsHaveValues = keys.every((key) => value[key] && value[key] !== '');
  if (!allFieldsHaveValues) {
    return 'Please enter a value for all fields. If you have no data for this field, please input 0.';
  }
  return true;
};

export const chainValidations = (results: (string | boolean)[]): string | boolean => {
  for (const result of results) {
    if (typeof result === 'string') {
      return result;
    }
  }
  return true;
};

export const sumEqualToPreviousAnswer = ({
  values,
  answers,
  question,
  previousAnswer,
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  previousAnswer: string;
}): { success: boolean; errorMessage?: string } => {
  const sum = Object.values(values).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );
  const noAnswerMessage = 'Please make sure you fill in the previous question first';
  if (!answers) return { success: false, errorMessage: noAnswerMessage };
  const answer = Object.keys(answers).find((key) => key.endsWith(previousAnswer || ''));
  if (!answer) return { success: false, errorMessage: noAnswerMessage };
  const answerValue = answers[answer];
  if (sum !== Number(answerValue)) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.sumEqualToPreviousAnswer ||
        `Totals to all questions must equal ${answerValue}. You have entered ${sum}`,
    };
  }
  return { success: true };
};

export const sumLessThanOrEqualToPreviousAnswer = ({
  values,
  answers,
  question,
  previousAnswer,
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  previousAnswer: string;
}): { success: boolean; errorMessage?: string } => {
  const sum = Object.values(values).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );
  const noAnswerMessage = 'Please make sure you fill in the previous question first';
  if (!answers) return { success: false, errorMessage: noAnswerMessage };
  const answer = Object.keys(answers).find((key) => key.endsWith(previousAnswer || ''));

  if (!answer) return { success: false, errorMessage: noAnswerMessage };
  const answerValue = answers[answer];
  if (sum > Number(answerValue)) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.sumLessThanOrEqualToPreviousAnswer ||
        `Total of ${sum} must be less than or equal to ${answerValue}`,
    };
  }
  return { success: true };
};

/**
 * FieldCompare is a custom validation function that compares the sum of the fields to the target
 *
 * @param operator - The operator to use for the comparison
 * @param values - The current question's values
 * @param answers - All previous answers
 * @param question - The current question's configuration object
 * @param fields - The array of field keys to be summed
 * @param answer - The the answer object to search
 * @param answerValue - The answer key to search for in the answer object
 * @param errorPrefix - The error prefix
 * @returns {success: boolean, errorMessage?: string} - The result of the validation
 */
export const fieldCompare = ({
  values,
  answers,
  question,
  fields,
  answer,
  answerValue,
  errorPrefix,
  operator = 'equal',
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  fields: string[];
  answer: string;
  answerValue: string;
  errorPrefix?: string;
  operator?: 'equal' | 'lessThanOrEqual';
}): { success: boolean; errorMessage?: string } => {
  // Get the previously answered value
  const missingAnswerMessage = 'Please make sure you fill in the previous question first';
  const answerKey = Object.keys(answers || {}).find((key) => key.endsWith(answer || ''));
  if (!answerKey || !answers?.[answerKey])
    return { success: false, errorMessage: missingAnswerMessage };
  const answerObject = stringToObject(answers[answerKey]);
  const target = Number.isNaN(Number(answerObject[answerValue]))
    ? 0
    : Number(answerObject[answerValue]);
  if (Number.isNaN(target)) return { success: false, errorMessage: missingAnswerMessage };

  // Get the values of the fields
  const fieldValuesFiltered = filterObjectByKeys(values, fields);
  const fieldSum = Object.values(fieldValuesFiltered).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );

  // Check if the fields are equal to the target
  if (operator === 'equal' && fieldSum !== target) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.fieldsEqual ||
        `${
          errorPrefix || `The total of ${fields.join(', ')}`
        } must equal ${target}, you have entered ${fieldSum}`,
    };
  }
  // Check if the fields are less than or equal to the target
  if (operator === 'lessThanOrEqual' && fieldSum > target) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.fieldsEqual ||
        `${errorPrefix || `The total of ${fields.join(', ')}`} must be less than or equal to ${target}, you have entered ${fieldSum}`,
    };
  }
  return { success: true };
};

// For a given object of <fieldKey, fieldValue>, return the sum of the values of the fields that match the row suffix
// If excludePattern is provided, filters out any values that match the excludePattern via regex
export const sumRow = (row: string, values: Record<string, string>, excludePattern?: string) => {
  const rowKeys = Object.keys(values).filter((key) => key.startsWith(row));
  const filteredRowKeys = excludePattern
    ? rowKeys.filter((key) => !key.match(excludePattern))
    : rowKeys;
  const rowValues = filteredRowKeys.map((key) => values[key]);
  return rowValues.reduce((acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)), 0);
};

export const objectToString = (object: Record<string, string>): string => {
  try {
    return JSON.stringify(object);
  } catch (_) {
    return '';
  }
};

export const stringToObject = (string?: string): Record<string, string> => {
  try {
    return JSON.parse(string || '{}');
  } catch (_) {
    return {};
  }
};
